import React from 'react';
import type { FC } from 'react';

import { faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';

import { Alert, Button, Modal } from '@shared/components/bootstrap';
import { usePolling } from '@shared/hooks/use_polling';

import { client } from '@admin/libraries/apollo';
import {
  useAutoDispatchDisabledRoutesExistQuery,
  useDispatcherRequestUpsertMutation,
  useDispatcherRequestQuery,
  Dispatcher__Request__Status,
} from '@admin/schema';

import { canUseDispatcherRequestFlow } from './permissions';

const DispatcherRequestDetailsDialog: FC<{
  when: string;
  requestID: string;
  onClose(): void;
}> = ({ requestID, when, onClose }) => {
  const { startPolling, stopPolling, data } = useDispatcherRequestQuery({
    client,
    variables: { id: requestID },
  });

  const status = data?.request.status;
  const processing = status === Dispatcher__Request__Status.Processing;

  usePolling({
    startPolling,
    stopPolling,
    poll: processing,
  });

  return (
    <Modal onClose={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Auto-Dispatch {when}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {processing ? (
            <Alert style="warning">
              <FontAwesomeIcon icon={faSpinner} spin /> Results may take <strong>30 minutes</strong> to generate.
            </Alert>
          ) : (
            <Alert style="success">
              <FontAwesomeIcon icon={faCheck} /> Results are generated. Please refresh to see the generated routes.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" kind="default" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const DispatcherRequestUpsertDialog: FC<{
  when: string;
  hasDisabledRoutes: boolean;
  error?: string | null;
  loading: boolean;
  save(): void;
  onClose(): void;
}> = ({ when, hasDisabledRoutes, error, loading, save, onClose }) => {
  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (loading) return;

    save();
  };

  return (
    <Modal onClose={onClose}>
      <form onSubmit={onSubmit}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Auto-Dispatch {when}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert style="danger">{error}</Alert>}
            <p>
              Are you sure you want to auto-dispatch <strong>{when}</strong>?
            </p>
            <ul>
              <li>
                Running an auto-dispatch <strong>re-evaluates and may rebuild</strong> routes that do not have{' '}
                <strong>Disable Auto-Dispatch for Route</strong> checked.
              </li>
              {hasDisabledRoutes && (
                <li>
                  Routes with <strong>Disable Auto-Dispatch for Route</strong> checked are skipped by the
                  auto-dispatcher but can still be edited manually.
                </li>
              )}
              <li>
                Running an auto-dispatch may take <strong>30 minutes</strong> to generate routes.
              </li>
            </ul>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" kind="default" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" kind="danger" loading={loading}>
              Confirm
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </form>
    </Modal>
  );
};

export const DispatcherRequestDialog: FC<{
  date: DateTime;
  regionID: string;
  onClose(): void;
}> = ({ date, regionID, onClose }) => {
  const canUseFlow = canUseDispatcherRequestFlow();
  const when = date.toLocaleString(DateTime.DATE_HUGE);
  const dateParam = date.toISODate() ?? '';

  const { data: disabledRoutesData } = useAutoDispatchDisabledRoutesExistQuery({
    client,
    variables: { regionID, date: dateParam },
    skip: !canUseFlow || !regionID || !dateParam,
  });

  const [execute, { loading, data }] = useDispatcherRequestUpsertMutation({
    client,
    variables: { regionID, date: dateParam },
  });

  const result = data?.result;
  const error = result?.error;
  const request = result?.request;
  const hasDisabledRoutes = disabledRoutesData?.autoDispatchDisabledRoutesExist ?? false;

  if (!canUseFlow) {
    return null;
  }

  return request ? (
    <DispatcherRequestDetailsDialog when={when} requestID={request.id} onClose={onClose} />
  ) : (
    <DispatcherRequestUpsertDialog
      when={when}
      hasDisabledRoutes={hasDisabledRoutes}
      error={error}
      loading={loading}
      save={execute}
      onClose={onClose}
    />
  );
};
